import {
  Cable,
  ChevronsDown,
  CircleParking,
  Eye,
  EyeOff,
  Footprints,
  Globe,
  Grip,
  House,
  IceCreamCone,
  Lock,
  Mountain,
  MountainSnow,
  Pyramid,
  Rabbit,
  Snowflake,
  Triangle,
  type LucideIcon,
} from "lucide-react";
import { getLocale } from "./locale";

export const ACTIVITIES = [
  "HIKING",
  "TRAIL_RUNNING",
  "SNOWSHOEING",
  "SKI_TOURING",
  "MOUNTAINEERING",
  "ROCK_CLIMBING",
  "ICE_CLIMBING",
  "VIA_FERRATA",
] as const;
export type Activity = (typeof ACTIVITIES)[number];

export const PLACE_TYPES = ["SUMMIT", "HUT", "PASS", "CRAG", "VIEWPOINT", "PARKING"] as const;
export type PlaceType = (typeof PLACE_TYPES)[number];

export const CONDITIONS = ["EXCELLENT", "GOOD", "MEDIOCRE", "POOR"] as const;
export type ConditionRating = (typeof CONDITIONS)[number];

// Most urgent first — same order as the `tick_priority` enum in the database,
// so this array matches what PRIORITY_ASC returns.
export const TICK_PRIORITIES = ["NEXT_UP", "THIS_SEASON", "SOMEDAY"] as const;
export type TickPriority = (typeof TICK_PRIORITIES)[number];

// Matches the `climb_style` enum (GraphQL `ClimbStyle`) on outing_route.
// Note: outing_route.result is a plain text column, not an enum.
export const CLIMB_STYLES = ["ONSIGHT", "FLASH", "REDPOINT", "ATTEMPT"] as const;
export type ClimbStyle = (typeof CLIMB_STYLES)[number];

export const OUTING_VISIBILITIES = ["PUBLIC", "FOLLOWERS", "PRIVATE"] as const;
export type OutingVisibility = (typeof OUTING_VISIBILITIES)[number];

// Completed first — it is the create form's default. PLANNED outings are
// owner-only via RLS until the owner explicitly marks them completed.
export const OUTING_STATUSES = ["COMPLETED", "PLANNED"] as const;
export type OutingStatus = (typeof OUTING_STATUSES)[number];

// Clubs are the database name for the product's "Groups" (`group` is a
// reserved SQL keyword). Wire values stay UPPERCASE like every other enum.
export const CLUB_VISIBILITIES = ["PUBLIC", "PRIVATE", "HIDDEN"] as const;
export type ClubVisibility = (typeof CLUB_VISIBILITIES)[number];

export const CLUB_JOIN_POLICIES = ["OPEN", "REQUEST_TO_JOIN", "INVITE_ONLY"] as const;
export type ClubJoinPolicy = (typeof CLUB_JOIN_POLICIES)[number];

export const CLUB_MEMBER_ROLES = ["OWNER", "ADMIN", "MEMBER"] as const;
export type ClubMemberRole = (typeof CLUB_MEMBER_ROLES)[number];

export const CLUB_MEMBER_STATUSES = ["ACTIVE", "PENDING", "INVITED", "BANNED"] as const;
export type ClubMemberStatus = (typeof CLUB_MEMBER_STATUSES)[number];

// Events (planned meetups, standalone or belonging to a club/"group").
export const EVENT_JOIN_POLICIES = ["OPEN", "REQUEST_TO_JOIN", "INVITE_ONLY", "MEMBERS_ONLY", "CLOSED"] as const;
export type EventJoinPolicy = (typeof EVENT_JOIN_POLICIES)[number];

export const EVENT_VISIBILITIES = ["PUBLIC", "MEMBERS", "PRIVATE"] as const;
export type EventVisibility = (typeof EVENT_VISIBILITIES)[number];

export const EVENT_RECURRENCES = ["NONE", "DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY"] as const;
export type EventRecurrence = (typeof EVENT_RECURRENCES)[number];

export const EVENT_PARTICIPANT_STATUSES = ["CONFIRMED", "PENDING", "INVITED", "DECLINED"] as const;
export type EventParticipantStatus = (typeof EVENT_PARTICIPANT_STATUSES)[number];

export type EventParticipantRole = "ORGANIZER" | "ATTENDEE";

export const ACTIVITY_ICONS: Record<Activity, LucideIcon> = {
  HIKING: Footprints,
  TRAIL_RUNNING: Rabbit,
  SNOWSHOEING: Snowflake,
  SKI_TOURING: MountainSnow,
  MOUNTAINEERING: Mountain,
  ROCK_CLIMBING: Grip,
  ICE_CLIMBING: IceCreamCone,
  VIA_FERRATA: Cable,
};

export const CLUB_VISIBILITY_ICONS: Record<ClubVisibility, LucideIcon> = {
  PUBLIC: Globe,
  PRIVATE: Lock,
  HIDDEN: EyeOff,
};

export const PLACE_TYPE_ICONS: Record<PlaceType, LucideIcon> = {
  SUMMIT: Triangle,
  HUT: House,
  PASS: ChevronsDown,
  CRAG: Pyramid,
  VIEWPOINT: Eye,
  PARKING: CircleParking,
};

/** "ROCK_CLIMBING" → "Rock climbing". Display goes through labelizeT (lib/i18n),
 * which uses this only as the fallback for values without an `enum.*` entry. */
export function labelize(value: string): string {
  const s = value.toLowerCase().replaceAll("_", " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatDistance(m: number | null | undefined): string {
  if (m == null) return "–";
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

export function formatElevation(m: number | null | undefined): string {
  return m == null ? "–" : `${Math.round(m)} m`;
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null) return "–";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h${m ? ` ${m}min` : ""}` : `${m}min`;
}

export function formatElapsed(seconds: number | null | undefined): string {
  if (seconds == null) return "–";
  return formatDuration(Math.round(seconds / 60));
}

/** Average moving pace, "9:30 /km". */
export function formatPace(secondsPerKm: number | null | undefined): string {
  if (secondsPerKm == null || !Number.isFinite(secondsPerKm)) return "–";
  const total = Math.round(secondsPerKm);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")} /km`;
}

export function formatSpeed(kmh: number | null | undefined): string {
  if (kmh == null || !Number.isFinite(kmh)) return "–";
  return `${kmh.toFixed(1)} km/h`;
}

export function formatDate(date: string | null | undefined): string {
  if (!date) return "–";
  return new Date(`${date}T00:00:00`).toLocaleDateString(getLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Date + time from a full timestamptz, e.g. "Aug 11, 2026, 11:00". */
export function formatDateTime(timestamp: string | null | undefined): string {
  if (!timestamp) return "–";
  return new Date(timestamp).toLocaleString(getLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Event time span from timestamptz strings: same-day spans collapse to one
 * date plus start–end times ("Aug 11, 2026 · 11:00 – 18:00"); multi-day spans
 * show the two dates ("Aug 22 – Aug 24, 2026" via Intl range formatting).
 */
export function formatDateRange(startsAt: string | null | undefined, endsAt?: string | null): string {
  if (!startsAt) return "–";
  const locale = getLocale();
  const start = new Date(startsAt);
  const dateOpts: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  if (!endsAt) return `${start.toLocaleDateString(locale, dateOpts)} · ${start.toLocaleTimeString(locale, timeOpts)}`;
  const end = new Date(endsAt);
  if (start.toDateString() === end.toDateString()) {
    return `${start.toLocaleDateString(locale, dateOpts)} · ${start.toLocaleTimeString(locale, timeOpts)} – ${end.toLocaleTimeString(locale, timeOpts)}`;
  }
  return new Intl.DateTimeFormat(locale, dateOpts).formatRange(start, end);
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/**
 * "3 hours ago" for comment timestamps. Takes a full timestamptz, unlike
 * formatDate which takes a bare `date` column.
 */
export function formatRelativeTime(timestamp: string | null | undefined): string {
  if (!timestamp) return "–";
  const seconds = (Date.now() - new Date(timestamp).getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(getLocale(), { numeric: "auto" });
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(-Math.round(seconds / size), unit);
  }
  return rtf.format(-Math.round(seconds), "second");
}
